/**
 * Background music (AudioPlayingController.Start: Resources/Audio/<name>,
 * looping, low volume). Browsers only allow audio after a user gesture, so if
 * autoplay is refused we start it on the first pointer/key event instead.
 */
import { useEffect } from "react";
import type { Project } from "@makeit/schema";
import { backgroundMusicUrl } from "../data/media";

const VOLUME = 0.15; // the app mixes it at ~0.09 under the effects

export function useBackgroundMusic(project: Project, enabled = true) {
  const url = enabled ? backgroundMusicUrl(project.settings.backgroundMusic) : null;
  useEffect(() => {
    if (!url) return;
    const audio = new Audio(url);
    audio.loop = true;
    audio.volume = VOLUME;
    audio.preload = "auto";
    let disposed = false;
    const events = ["pointerdown", "keydown", "touchstart"] as const;
    const unarm = () => events.forEach((e) => window.removeEventListener(e, onGesture, true));
    const onGesture = () => {
      unarm();
      if (!disposed) void audio.play().catch(() => undefined);
    };
    const arm = () => events.forEach((e) => window.addEventListener(e, onGesture, { capture: true, passive: true }));
    audio.play().catch(arm);
    return () => {
      disposed = true;
      unarm();
      audio.pause();
      audio.src = "";
    };
  }, [url]);
}
